import { FilterTypes, FilterValueTypes, validateFilterStructure } from "../../../handlers/filter";
import { ServiceStatus } from "../../../types";
import axios from "axios";
import { SEGMENT_TTL } from "../../..";
import { PromiseHandler } from "../../../handlers";
import { isRetargetingSegment, orgSegmentPreferenceToServiceOrgSegmentPreference, retargetingSegmentToRetargetingSegmentCreationPayload, retargetingSegmentToRetargetingSegmentUpdatePayload, segmentToUpdatePayload, serviceSegmentToSegment } from "../../../models/dataManagementPlatform/segment";
import { MadSDKAbortError, SegmentSaveNotImplemented, UnauthenticatedError } from "../../../errors";
import { DmpSegmentsFetchFailed, DmpSegmentsUpsertFailed } from "./errors";
import { DEFAULT_SEGMENT_FILTERS } from "./constants";
import { toQueryParams } from "./filters";
/**
 * Segments module for managing DMP segments with configurable persistent caching.
 *
 * When the 'enable_madsdk_persistent_caching' feature flag is enabled, mutations
 * surgically update the persistent cache rather than clearing it entirely.
 *
 * When disabled, mutations clear all resource caches (default cache behavior).
 */
class Segments extends PromiseHandler {
    constructor(sdk) {
        const segmentsUrl = `${sdk.urls.baseAPIUrl}/dmpsegments`;
        const PERSISTENT_CACHE_URLS = [`${segmentsUrl}${toQueryParams(DEFAULT_SEGMENT_FILTERS)}`];
        super(sdk, "segments", { atomize: true, persistResources: PERSISTENT_CACHE_URLS });
        this.toDmpSegment = (dmp) => {
            return serviceSegmentToSegment(dmp);
        };
        /**
         * @param error: the error to whittle down into something more specific/segment-or-sdk-related.
         * @return: an appropriate Error if a specific error can be determined.
         */
        this.getSpecificSegmentError = (error) => {
            if (axios.isCancel(error)) {
                return new MadSDKAbortError();
            }
            return new DmpSegmentsFetchFailed(error);
        };
        /**
         * @param error: the error to whittle down into something more specific/segment-or-sdk-related.
         * @return: an appropriate Error if a specific error can be determined.
         */
        this.upsertSegmentError = (error) => {
            if (axios.isCancel(error)) {
                return new MadSDKAbortError();
            }
            return new DmpSegmentsUpsertFailed(error);
        };
        this.url = {
            multiple: segmentsUrl,
            preference: `${this.sdk.urls.baseAPIUrl}/segment/preferences`
        };
    }
    async findItems(filters) {
        // Auto-apply default status filter (READY + EXPIRED) if no where clause provided (ie. mad-hooks passes { where: [] } as default)
        const hasNoWhereFilters = !filters?.where || filters.where.length === 0;
        const effectiveFilters = hasNoWhereFilters
            ? { ...filters, where: DEFAULT_SEGMENT_FILTERS.where }
            : filters;
        // Assert that filters are of valid structure. This will throw if not.
        validateFilterStructure(effectiveFilters, [
            { filterType: FilterTypes.IN, valueType: FilterValueTypes.OBJECT },
            { filterType: FilterTypes.EQ, valueType: FilterValueTypes.STRING },
            { filterType: FilterTypes.EQ, valueType: FilterValueTypes.NUMBER }
        ]);
        const url = `${this.url.multiple}${toQueryParams(effectiveFilters)}`;
        return this.getSegments(url);
    }
    /**
     * @param url: endpoint to hit
     * @return: all segments
     */
    getSegments(url) {
        return this.cache.promise(url, () => axios
            .get(url, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            return res.data.map((segment) => this.toDmpSegment(segment));
        })
            .catch((error) => {
            throw this.getSpecificSegmentError(error);
        }));
    }
    async createRetargetingSegment(payload) {
        // black box magic to generate a valid key since backend requires it for now
        async function newKey() {
            async function base64Arraybuffer(data) {
                const base64url = await new Promise((r) => {
                    const reader = new FileReader();
                    reader.onload = () => r(reader.result);
                    reader.readAsDataURL(new Blob([data]));
                });
                return base64url.split(",", 2)[1];
            }
            for (let i = 0; i < 100000; i++) {
                const key = crypto.getRandomValues(new Uint8Array(21));
                const encoded = await base64Arraybuffer(key);
                if (/[+/]/.test(encoded))
                    continue;
                const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", key));
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                /* @ts-ignore */
                if (digest.at(-1) === 101)
                    return encoded;
            }
            return;
        }
        const url = `${this.sdk.urls.baseAPIUrl}/segment`;
        const id = await newKey();
        const finalPayload = {
            id,
            external_id: id,
            name: payload.name,
            description: payload.description,
            parent: payload.parent,
            retargeting: {
                [payload.retargetingType]: payload.target,
                ...(payload.retargetingWindow && { retargetingWindow: payload.retargetingWindow }),
                ...(payload.eventType && { eventType: payload.eventType }),
                ...(payload.eventSubtype && { eventSubtype: payload.eventSubtype }),
                ...(payload.parameterFilters && { parameterFilters: payload.parameterFilters }),
                ...(payload.parameterFilterOperator && {
                    parameterFilterOperator: payload.parameterFilterOperator
                })
            },
            preferred: false,
            status: ServiceStatus.READY
        };
        const response = await axios.post(url, finalPayload, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        // need to refetch retargeting segment as creation call doesn't return all fields
        const retargetSegment = (await this.find_once({
            where: [{ field: "id", type: FilterTypes.EQ, value: response.data.data.id }]
        }));
        this.cache.reconcile("save", retargetSegment);
        return retargetSegment;
    }
    async updateRetargetingSegment(payload) {
        const url = `${this.sdk.urls.baseAPIUrl}/segment`;
        const finalPayload = {
            id: payload.id,
            name: payload.name,
            parent: payload.parent,
            description: payload.description,
            retargeting: {
                [payload.retargetingType]: payload.target,
                ...(payload.retargetingWindow && { retargetingWindow: payload.retargetingWindow }),
                ...(payload.eventType && { eventType: payload.eventType }),
                ...(payload.eventSubtype && { eventSubtype: payload.eventSubtype }),
                ...(payload.parameterFilters && { parameterFilters: payload.parameterFilters }),
                ...(payload.parameterFilterOperator && {
                    parameterFilterOperator: payload.parameterFilterOperator
                })
            },
            external_id: payload.externalId,
            preferred: !!payload.preferred,
            status: payload.status
        };
        const response = await axios
            .post(url, finalPayload, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(async (result) => {
            const user = this.sdk.getCurrentUser();
            if (typeof user === "undefined") {
                throw new UnauthenticatedError();
            }
            const preference = {
                orgId: user.parent || "",
                segmentId: result.data.data.id,
                preferred: !!payload.preferred
            };
            // Wait for preference to be saved before returning
            await axios.post(this.url.preference, [this.fromOrgSegmentPreference(preference)], {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            return result;
        });
        const retargetingSegment = this.toDmpSegment(response.data.data);
        retargetingSegment.preferred = !!payload.preferred;
        this.cache.reconcile("save", retargetingSegment);
        return retargetingSegment;
    }
    async updateFirstPartySegment(payload) {
        const url = `${this.sdk.urls.baseAPIUrl}/segment`;
        const finalPayload = {
            id: payload.id,
            name: payload.name,
            parent: payload.parent,
            description: payload.description,
            external_id: payload.externalId,
            preferred: !!payload.preferred,
            status: payload.status
        };
        return new Promise((resolve, reject) => {
            axios
                .post(url, finalPayload, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(async (res) => {
                const user = this.sdk.getCurrentUser();
                if (typeof user === "undefined") {
                    throw new UnauthenticatedError();
                }
                const segment = this.toDmpSegment(res.data.data);
                axios
                    .post(this.url.preference, [
                    {
                        org_id: user.parent || "",
                        segment_id: segment.id,
                        preferred: !!payload.preferred
                    }
                ], {
                    headers: {
                        "Content-Type": "application/json"
                    }
                })
                    .then(() => {
                    segment.preferred = !!payload.preferred;
                    this.cache.reconcile("save", segment);
                    resolve(segment);
                })
                    .catch((error) => {
                    reject(this.upsertSegmentError(error));
                });
            })
                .catch((error) => {
                reject(this.upsertSegmentError(error));
            });
        });
    }
    // special case because an org preference is the only field that can be updated
    async updateThirdPartySegment(payload) {
        const user = this.sdk.getCurrentUser();
        if (typeof user === "undefined") {
            throw new UnauthenticatedError();
        }
        const finalPayload = [
            {
                org_id: user.parent || "",
                segment_id: payload.id,
                preferred: !!payload.preferred
            }
        ];
        // Use find_once to get segment from cache or API
        const thisSegment = await this.find_once({
            where: [
                {
                    type: FilterTypes.EQ,
                    field: "id",
                    value: payload.id
                }
            ]
        });
        if (!thisSegment) {
            throw new Error(`Segment not found: ${payload.id}`);
        }
        return new Promise((resolve, reject) => {
            axios
                .post(this.url.preference, finalPayload, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(() => {
                thisSegment.preferred = !!payload.preferred;
                this.cache.reconcile("save", thisSegment);
                resolve(thisSegment);
            })
                .catch((error) => {
                reject(this.upsertSegmentError(error));
            });
        });
    }
    async saveItem(dmp) {
        const user = this.sdk.getCurrentUser();
        if (!user) {
            throw new UnauthenticatedError();
        }
        const isRetargeting = isRetargetingSegment(dmp);
        const isFirstParty = dmp.parent === user.parent;
        const isThirdParty = !isRetargeting && !isFirstParty;
        // CREATE - only retargeting segments can be created
        if (!dmp.id) {
            if (!isRetargeting) {
                throw new SegmentSaveNotImplemented();
            }
            const savePayload = retargetingSegmentToRetargetingSegmentCreationPayload(dmp);
            return this.createRetargetingSegment(savePayload);
        }
        if (isRetargeting) {
            const updatePayload = retargetingSegmentToRetargetingSegmentUpdatePayload(dmp);
            return this.updateRetargetingSegment(updatePayload);
        }
        const updatePayload = segmentToUpdatePayload(dmp);
        if (isFirstParty) {
            return this.updateFirstPartySegment(updatePayload);
        }
        else if (isThirdParty) {
            return this.updateThirdPartySegment(updatePayload);
        }
        throw new SegmentSaveNotImplemented();
    }
    async updateSegmentStatus(id, status) {
        const segment = await this.find_once({
            where: [
                {
                    field: "id",
                    type: FilterTypes.EQ,
                    value: id
                },
                {
                    field: "status",
                    type: FilterTypes.IN,
                    value: [ServiceStatus.READY, ServiceStatus.ARCHIVED, ServiceStatus.EXPIRED]
                }
            ]
        });
        const user = this.sdk.getCurrentUser();
        if (typeof user === "undefined") {
            throw new UnauthenticatedError();
        }
        if (!segment || (!("target" in segment) && segment.parent !== user.primaryOrganizationId)) {
            throw new Error("Only first party and retargeting segments can be updated");
        }
        // overwrite provided status value to enforce rules about transitions from archived status (500)
        if (segment.status === ServiceStatus.ARCHIVED) {
            const today = new Date().valueOf();
            if (isRetargetingSegment(segment) ||
                (segment.lastRefreshed && segment.lastRefreshed.valueOf() > today - SEGMENT_TTL)) {
                status = ServiceStatus.READY;
            }
            else {
                status = ServiceStatus.EXPIRED;
            }
        }
        const updatePayload = "retargetingType" in segment
            ? {
                id: segment.id,
                name: segment.label,
                parent: segment.parent,
                retargeting: {
                    [segment.retargetingType]: segment.target,
                    ...(segment.retargetingWindow && { retargetingWindow: segment.retargetingWindow }),
                    ...(segment.eventType && { eventType: segment.eventType }),
                    ...(segment.eventSubtype && { eventSubtype: segment.eventSubtype }),
                    ...(segment.parameterFilters && { parameterFilters: segment.parameterFilters }),
                    ...(segment.parameterFilterOperator && {
                        parameterFilterOperator: segment.parameterFilterOperator
                    })
                },
                external_id: segment.externalId,
                status
            }
            : {
                id: segment.id,
                name: segment.label,
                parent: segment.parent,
                external_id: segment.externalId,
                status
            };
        const response = await axios.post(`${this.sdk.urls.baseAPIUrl}/segment`, updatePayload, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        const updatedSegment = this.toDmpSegment(response.data.data);
        updatedSegment.createdBy = segment.createdBy;
        updatedSegment.createdOn = segment.createdOn;
        updatedSegment.preferred = segment.preferred;
        this.reconcileCache("save", updatedSegment);
        return updatedSegment;
    }
    async deleteItem(id) {
        const segment = await this.updateSegmentStatus(id, ServiceStatus.ARCHIVED);
        // A Maverick persona embeds its segments' statuses, so archiving a segment
        // leaves the cached personas view stale. Clear it here where the mutation
        // lives
        this.clearPersonasCache();
        return segment;
    }
    async unarchiveItem(id) {
        const segment = await this.updateSegmentStatus(id, ServiceStatus.READY);
        this.clearPersonasCache();
        return segment;
    }
    clearPersonasCache() {
        this.sdk.caches.clear("personas");
    }
    /**
     * Converts from a client org segment preference to a service org segment preference.
     * @param segment: the segment to convert.
     * @return: the converted segment.
     */
    fromOrgSegmentPreference(orgSegmentPreference) {
        return orgSegmentPreferenceToServiceOrgSegmentPreference(orgSegmentPreference);
    }
}
export default Segments;
export { DEFAULT_SEGMENT_FILTERS } from "./constants";
//# sourceMappingURL=index.js.map